import { iono, Element } from 'global';
import en from './languages/en.json';
import { defaultOptions } from '../utils/options';

const layouts = {};
const locales = {};

/**
 * Override base destroy method so that we can clean up ui related resources
 */
const createDestroyFn = instance => {
  const _destroy = instance.destroy;
  return _ => {
    instance.detach();
    instance.$container.removeAttribute('data-player-id');
    instance.$container.innerHTML = '';
    _destroy.call(instance);
  };
};

class PlayerUI extends iono.Player {
  constructor (container, options) {
    super(options);

    this.layout = false;
    this.locale = null;
    this.handleEvent = this.handleEvent.bind(this);

    const language = locales[this.options.ui.language] ? this.options.ui.language : defaultOptions.ui.language;
    this.options.ui.language = null;

    this.setContainer(container);
    this.setDisplayLanguage(language);
    this.setContentType(this.options.ui.contentType);

    this.setLayout(this.options.ui.layout, this.options.ui);

    // this.ready = PlayerUI.prototype.ready.bind(this);
    this.destroy = createDestroyFn(this);
  }

  /**
   * Retrieve the player container element
   *
   * @return {Element}
   */
  getContainer () {
    return this.$container;
  }

  /**
   * Set the player ui container element
   *
   * @param {Element} container
   */
  setContainer (container) {
    if (!(container instanceof Element)) {
      throw new TypeError('Unexpected container value: must be a valid DOM element');
    }
    container.setAttribute('data-player-id', this.id);
    this.$container = container;
  }

  /**
   * Retrieve the current theme
   *
   * @return {Object}
   */
  getTheme () {
    return this.getLayout().getTheme();
  }

  /**
   * Modify the current theme
   *
   * @param {Object|String} theme    A new theme colour configuration or the key to a theme colour variable (eg. accent)
   * @param {String}        hexColor (Optional) The colour used when called with a theme colour variable key
   */
  setTheme (theme, hexColor) {
    // ensure that when more than one argument is passed, the first argument is a string
    if (arguments.length > 1 && typeof theme !== 'string') {
      throw new TypeError('Unexpected value for theme: must be a string');
    }

    // ensure that when more than one argument is passed, the second argument is a string
    if (arguments.length > 1 && typeof hexColor !== 'string') {
      throw new TypeError('Unexpected value for hexColor: must be a string');
    }

    // ensure that when a single argument is passed, that argument must be an object
    if (arguments.length === 1 && typeof theme !== 'object') {
      throw new TypeError('Unexpected value for theme: must be an object');
    }

    // modify a single theme variable when more than one argument is passed
    if (arguments.length > 1) {
      this.options.ui.theme[theme] = hexColor;
    } else {
      this.options.ui.theme = Object.assign({}, this.options.ui.theme, theme);
    }

    // update the current layout theme
    this.ready(function () {
      this.getLayout().setTheme(this.options.ui.theme);
    });
  }

  /**
   * Retrieve the currently set layout
   *
   * @return {Layout}
   */
  getLayout () {
    return this.layout;
  }

  /**
   * Change the ui layout
   *
   * @param {String|Layout} name
   */
  setLayout (name, options = {}) {
    // wait for the player to be ready before changing layouts
    this.ready(function () {
      let InstanceLayout;

      if (typeof name !== 'string') {
        InstanceLayout = name;
      } else if (!layouts[name]) {
        return false;
      }

      // no need to change layout if they are the same
      if (typeof name === 'string') {
        if (this.layout && name === this.options.ui.layout) {
          return this.layout;
        }
        InstanceLayout = layouts[name];
      }

      if (this.layout) {
        this.layout.destroy();
      }

      if (!InstanceLayout) {
        return false;
      }

      const opts = Object.assign({}, this.options.ui, options);
      opts.theme = Object.assign({}, defaultOptions.ui.theme, options.theme);

      this.layout = new InstanceLayout(this, opts);
      this.options.ui.layout = name;
      this.layout.init(this.options.ui);
      this.render();
    });
  }

  /**
   * Retrieve the currently set display language
   *
   * @return {String}
   */
  getDisplayLanguage () {
    return this.options.ui.language;
  }

  /**
   * Change the display language
   *
   * @param {String} lang
   *
   * @return {Object}
   */
  setDisplayLanguage (lang) {
    if (!locales[lang]) {
      this.logger.warn(`[iono.Player.UI] Cannot set display language to '${lang}': The locale has not been loaded`);
      return false;
    }

    if (this.options.ui.language === lang) return this;

    this.locale = locales[lang];
    this.options.ui.language = lang;
    this.render();

    return this;
  }

  /**
   * Retrieve the current player content type
   *
   * @todo move to legacy layout
   * @return {String}
   */
  getContentType () {
    return this.options.ui.contentType;
  }

  /**
   * Change the ui content type
   *
   * @todo move to legacy layout
   * @param {String} contentType
   */
  setContentType (contentType) {
    if (contentType === this.options.ui.contentType) return;

    this.options.ui.contentType = contentType;

    // re-render the layout if the content types differ
    this.render();

    return this;
  }

  /**
   * Pass player events to layout event handlers
   *
   * @param {Object} event Core player event
   */
  handleEvent (event) {
    const fn = this.layout && this.layout[`on${event.type}`];

    if (typeof fn === 'function') {
      fn.call(this.layout, event);
    }
  }

  /**
   * Attach the player ui to the DOM
   *
   * @param {DOMElement} element The element to be attached
   */
  attach (element = this.$container) {
    this.layout.attach(element);
    this.on('all', this.handleEvent);
  }

  /**
   * Detach the player ui from the DOM
   */
  detach () {
    this.layout.detach();
    this.off('all', this.handleEvent);
  }

  /**
   * Render the player ui to the DOM
   *
   * @param {DOMElement} element The element to render to
   */
  render () {
    if (!this.layout) return;
    this.ready(_ => {
      this.$container.innerHTML = this.layout.render();
      this.attach(this.$container.firstElementChild);
    });
  }

  /**
   * Define a layout for the player ui to use
   *
   * @param {String} name
   * @param {Layout} Layout
   *
   * @return {Layout}
   */
  static defineLayout (name, Layout) {
    layouts[name] = Layout;
    return layouts[name];
  }

  /**
   * Define a locale for the player UI to use
   *
   * @param {String} name   The locale name eg. en
   * @param {Object} locale The translations dictionary for the locale
   *
   * @return {Object}
   */
  static defineLocale (name, locale) {
    locales[name] = locale;
    return locales[name];
  }

  /**
   * Retrieve a locale from the loaded list of locales
   *
   * @param {String} name The locale name eg. en
   *
   * @returns {Object} The translations dictionary for the locale
   */
  static getLocale (name) {
    return locales[name] || locales.en;
  }
}

// define the default locale: English
PlayerUI.defineLocale('en', en);

export default PlayerUI;
